import { SetStateAction, useEffect, useState } from 'react';
import {
  getBenchmarkStatistics,
  getStatistics,
  Period,
  ComparisonPeriod,
} from '../../../services/api/statistics/statistics';
import { User } from '@tassomutuo-backoffice/types';
import { useQuery } from '@tanstack/react-query';
import { getAllTeamGroup } from '../../../services/api/team/team';
import {
  getDashboardFiltersInSessionStorage,
  setDashboardFiltersInSessionStorage,
} from '../../../services/sessionStorage/sessionStorage';

export interface DateRange {
  start: Date | null;
  end: Date | null;
}

const useStatistics = ({
  from,
}: {
  from: 'dashboard' | 'deep-dive' | 'fatturato-perso';
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('last_3_months');
  const [selectedComparisonPeriod, setSelectedComparisonPeriod] =
    useState<ComparisonPeriod>('previous_period');
  const [selectedMember, setSelectedMember] = useState<Pick<
    User,
    'id' | 'hubspotOwnerId' | 'name' | 'email'
  > | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>('');

  // Singolo stato per il date range
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });

  // Determina se stiamo usando un range personalizzato
  const isUsingCustomDateRange = dateRange.start && dateRange.end;

  // Query principale per le statistiche
  const { data, isPending, error } = useQuery({
    queryKey: [
      'statistics',
      selectedPeriod,
      selectedGroupId,
      dateRange.start,
      dateRange.end,
      selectedComparisonPeriod,
    ],
    queryFn: () => {
      // Se stiamo usando un range personalizzato, passa le date invece del periodo
      if (isUsingCustomDateRange && dateRange.start && dateRange.end) {
        return getStatistics(
          undefined, // period non utilizzato quando si usa date range
          undefined, // secondo parametro (se esiste)
          selectedGroupId,
          dateRange.start,
          dateRange.end,
          selectedComparisonPeriod
        );
      }
      // Altrimenti usa il periodo predefinito
      return getStatistics(
        selectedPeriod,
        undefined,
        selectedGroupId,
        undefined,
        undefined,
        selectedComparisonPeriod
      );
    },
    enabled: !!(selectedPeriod || isUsingCustomDateRange),
  });

  // Query per le statistiche benchmark
  const {
    data: benchmarkStatistics,
    // isPending: isPendingBenchmark,
    // error: errorBenchmark,
  } = useQuery({
    queryKey: [
      'benchmarkStatistics',
      selectedPeriod,
      dateRange.start,
      dateRange.end,
    ],
    queryFn: () => {
      // Stessa logica per le statistiche benchmark
      if (isUsingCustomDateRange && dateRange.start && dateRange.end) {
        return getBenchmarkStatistics(
          undefined,
          dateRange.start,
          dateRange.end
        );
      }
      return getBenchmarkStatistics(selectedPeriod);
    },
    enabled: !!(selectedPeriod || isUsingCustomDateRange),
  });

  // Query per i team groups (rimane invariata)
  const {
    data: teamGroups,
    // error,
  } = useQuery({
    queryKey: ['TeamGroups', data?.team],
    queryFn: () => getAllTeamGroup(data?.team || ''),
    enabled: !!data?.isTeamStatistics && !!data?.team,
  });

  useEffect(() => {
    const filters = getDashboardFiltersInSessionStorage();
    if (filters) {
      if (filters.selectedPeriod) {
        setSelectedPeriod(filters.selectedPeriod);
      }
      if (filters.groupId) {
        setSelectedGroupId(filters.groupId);
      }
      if (
        (from === 'dashboard' || from === 'fatturato-perso') &&
        filters.selectedMember
      ) {
        setSelectedMember(filters.selectedMember);
      }
      if (filters.dateRange?.start && filters.dateRange?.end) {
        setDateRange({
          start: new Date(filters.dateRange.start),
          end: new Date(filters.dateRange.end),
        });
      }
    }
  }, []);

  const handleSelectPeriod = (period: Period) => {
    setSelectedPeriod(period);
    setDashboardFiltersInSessionStorage({ selectedPeriod: period });
  };

  const handleSelectGroupId = (groupId: string | null) => {
    setSelectedGroupId(groupId);
    setDashboardFiltersInSessionStorage({ groupId });
  };

  const handleSelectMember = (
    member: Pick<User, 'id' | 'hubspotOwnerId' | 'name' | 'email'> | null
  ) => {
    setSelectedMember(member);
    setDashboardFiltersInSessionStorage({ selectedMember: member });
  };

  const handleDateRangeChange = (value: SetStateAction<DateRange>) => {
    setDateRange((prevValue) => {
      // Calcola il nuovo valore
      const newValue = typeof value === 'function' ? value(prevValue) : value;

      setDashboardFiltersInSessionStorage({ dateRange: newValue });

      return newValue;
    });
  };

  const getFilterText = () => {
    const filters = [];

    if (selectedPeriod && !dateRange?.start) {
      filters.push(`Periodo: ${selectedPeriod}`);
    }
    if (dateRange?.start && dateRange?.end) {
      filters.push(
        `Periodo: dal ${dateRange.start.toLocaleDateString(
          'it-IT'
        )} al ${dateRange.end.toLocaleDateString('it-IT')}`
      );
    }
    if (selectedGroupId) {
      const selectedGroup = teamGroups?.find(
        (group) => group.id === selectedGroupId
      );
      if (selectedGroup) {
        filters.push(`${selectedGroup.name}`);
      }
    }
    if (selectedMember) {
      filters.push(`Consulente: ${selectedMember.email}`);
    }

    return filters.join(' | ');
  };

  const memberSelectOptions = data?.memberStatistics?.map(
    (memberStats) => memberStats.user
  );

  const statisticsToShow = selectedMember
    ? data?.memberStatistics?.find(
        (mStat) => mStat.user.id === selectedMember.id
      )?.statistics
    : data;

  const teamMembersStatistics = data?.memberStatistics;

  const lostRevenueLeads = selectedMember
    ? data?.lostRevenueLeads?.filter(
        (lead) => lead.hubspot_owner_id === selectedMember.hubspotOwnerId
      ) || []
    : data?.lostRevenueLeads || [];

  return {
    isPending,
    error,
    isTeamStatistics: !!data?.isTeamStatistics,
    selectedPeriod,
    setSelectedPeriod: handleSelectPeriod,
    selectedComparisonPeriod,
    setSelectedComparisonPeriod,
    selectedMember,
    setSelectedMember: handleSelectMember,
    teamGroups,
    memberSelectOptions,
    selectedGroupId,
    setSelectedGroupId: handleSelectGroupId,
    statisticsToShow,
    benchmarkStatistics,
    teamMembersStatistics,
    dateRange,
    setDateRange: handleDateRangeChange,
    isUsingCustomDateRange,
    lostRevenueLeads,
    getFilterText,
  };
};

export default useStatistics;
